/**
 * DataTableControl
 * Renders a sortable data table from config.columns[] and value (array of objects).
 * Config: columns[] = { key: string; label: string; sortable?: boolean }
 * Value: Record<string, unknown>[]
 */

import React, { useState, memo, useMemo, useCallback } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import { ArrowUpIcon, ArrowDownIcon } from '@atlas-forms/ui-components-react';

export interface DataTableColumn {
  key: string;
  label: string;
  sortable?: boolean;
}

export interface DataTableControlProps {
  control: FormControl;
  value?: Record<string, unknown>[];
  onChange?: (v: Record<string, unknown>[]) => void;
  mode?: 'edit' | 'view';
}

type SortDir = 'asc' | 'desc';

export const DataTableControl = memo(function DataTableControl({ control, value, mode = 'edit' }: DataTableControlProps) {
  const cfg = (control as any).config ?? {};
  const columns: DataTableColumn[] = cfg.columns ?? [];
  const rows: Record<string, unknown>[] = Array.isArray(value) ? value : [];
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const wrapStyle: React.CSSProperties = {
    overflowX: 'auto',
    borderRadius: 8,
    border: '1px solid rgba(51,65,85,0.3)',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  };

  const thStyle = (col: DataTableColumn): React.CSSProperties => ({
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#94a3b8',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: 'rgba(15,23,42,0.6)',
    borderBottom: '1px solid rgba(51,65,85,0.3)',
    cursor: col.sortable && mode !== 'view' ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  });

  const tdStyle: React.CSSProperties = {
    padding: '9px 12px',
    borderBottom: '1px solid rgba(51,65,85,0.15)',
    color: '#e2e8f0',
    fontSize: 13,
  };

  const emptyStyle: React.CSSProperties = {
    padding: '32px',
    textAlign: 'center',
    color: '#475569',
    fontSize: 13,
  };

  const handleSort = useCallback((col: DataTableColumn) => {
    if (!col.sortable || mode === 'view') return;
    setSortKey((key) => {
      if (key === col.key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      } else {
        setSortDir('asc');
        return col.key;
      }
    });
  }, [mode]);

  const sorted = useMemo(() => {
    return sortKey
      ? [...rows].sort((a, b) => {
          const av = a[sortKey] ?? '';
          const bv = b[sortKey] ?? '';
          const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
          return sortDir === 'asc' ? cmp : -cmp;
        })
      : rows;
  }, [sortKey, sortDir, rows]);

  if (columns.length === 0) {
    return (
      <div data-control-id={control.id} data-control-type="data-table" style={emptyStyle}>
        No columns configured
      </div>
    );
  }

  return (
    <div data-control-id={control.id} data-control-type="data-table" style={wrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={thStyle(col)} onClick={() => handleSort(col)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {col.label}
                  {col.sortable && sortKey === col.key && (
                    sortDir === 'asc'
                      ? <ArrowUpIcon width={12} height={12} />
                      : <ArrowDownIcon width={12} height={12} />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={emptyStyle}>No data</td>
            </tr>
          ) : (
            sorted.map((row, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <td key={col.key} style={tdStyle}>
                    {String(row[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
});
